import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Button, TextBox } from "@src/components";
import { Common } from "@src/styles/Common";
import { INVALID_VALUE } from "@src/constants/InvalidValue";
import { ProvisionUser } from "@src/interfaces/Commodity.interface";
import React, { Dispatch, SetStateAction, useMemo } from "react";
import { getUserColumns } from "./PartnerUserColumns";

interface PartnerBenefitsUserProps {
    userParams: string[];
    setUserParams: (v: string[]) => void;
    onSearch: () => void;
    onResetSearch: () => void;
    users: ProvisionUser[];
    selectedUser: ProvisionUser | null;
    setSelectedUser: Dispatch<SetStateAction<ProvisionUser | null>>;
    isViewModal?: boolean;
}

const PartnerBenefitsUser = ({
    userParams,
    setUserParams,
    onSearch,
    onResetSearch,
    users,
    selectedUser,
    setSelectedUser,
    isViewModal = false,
}: PartnerBenefitsUserProps) => {
    const columns = useMemo(() => getUserColumns(), []);

    return (
        <StyledPartnerUserWrapper>
            <div className="searchRow">
                <div className="textBoxArea">
                    <TextBox
                        handleChange={(value) => {
                            setUserParams(value.split(","));
                        }}
                        label=""
                        maxLength={500}
                        placeholder="아이디(이메일) 혹은 전화번호를 입력해주세요."
                        size="small"
                        isDisabled={isViewModal}
                        defaultText={userParams.join(",")}
                    />
                    <div className="description">
                        검색 결과에서 한 명만 선택한 뒤 추가할 수 있습니다. 여러 조건은
                        {'","'}로 구분해 검색할 수 있습니다.{"("}예 abc@example.com,def@example.com{")"}
                    </div>
                </div>
                <Button
                    color="purple"
                    handleClick={onSearch}
                    label="검색"
                    size="small"
                    styles={{
                        width: 68,
                        height: 34,
                        marginLeft: 10,
                        flexShrink: 0,
                    }}
                    isDisabled={isViewModal}
                />
                <Button
                    color="white"
                    handleClick={onResetSearch}
                    label="검색 초기화"
                    size="small"
                    styles={{
                        minWidth: 96,
                        height: 34,
                        marginLeft: 8,
                        flexShrink: 0,
                    }}
                    isDisabled={isViewModal}
                />
            </div>

            <div css={searchResultList}>
                <table>
                    <thead>
                        <tr>
                            {columns.map((column: any) => (
                                <th key={String(column.accessor)}>{column.Header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="noData">
                                    조회된 회원이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            users.map((item, idx) => {
                                const isSelected = selectedUser?.no === item.no && selectedUser?.userNo === item.userNo;
                                return (
                                    <tr
                                        key={`${item.no}-${item.userNo}-${idx}`}
                                        className={isSelected ? "selected" : ""}
                                        onClick={() => setSelectedUser(item)}
                                    >
                                        {columns.map((column: any) => {
                                            const key = String(column.accessor);
                                            const value = (item as any)?.[key];
                                            return <td key={key}>{value ? value : INVALID_VALUE}</td>;
                                        })}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </StyledPartnerUserWrapper>
    );
};

export default PartnerBenefitsUser;

const StyledPartnerUserWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 25px;
    font-weight: 400;
    font-size: 13px;
    color: #000000;

    .searchRow {
        display: flex;
        align-items: flex-start;
        width: 100%;
        & > .textBoxArea {
            flex: 1;
            min-width: 0;
            & > .description {
                width: 100%;
                word-break: break-all;
                font-weight: 400;
                font-size: 10px;
                color: #6d6d6d;
                margin-top: 7px;
            }
        }
    }
`;

const searchResultList = css`
    width: 100%;
    max-height: 320px;
    overflow: auto;
    border: 1px solid ${Common.colors.gray100};
    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }
    thead {
        position: sticky;
        top: 0;
        z-index: 1;
        background: #fff;
    }
    th,
    td {
        border-bottom: 1px solid ${Common.colors.gray100};
        padding: 12px 10px;
        text-align: center;
        ${Common.textStyle.text1_R13}
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    th {
        font-weight: 600;
    }
    th:nth-of-type(1),
    td:nth-of-type(1) {
        width: 70px;
    }
    th:nth-of-type(2),
    td:nth-of-type(2) {
        width: 90px;
    }
    td:nth-of-type(1) > div,
    td:nth-of-type(2) > div {
        min-width: 0 !important;
    }
    tbody tr {
        cursor: pointer;
    }
    tbody tr:hover {
        background-color: ${Common.colors.gray100};
    }
    tbody tr.selected {
        background-color: rgba(154, 73, 242, 0.15);
    }
    .noData {
        height: 220px;
        text-align: center;
        vertical-align: middle;
        ${Common.textStyle.body3_R14};
        background-color: rgba(0, 0, 0, 0.1);
    }
`;
